import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import {
  RelatedArticlesBand,
  RelatedArticlesSidebar,
  type RelatedArticle,
} from "@/components/gisela/RelatedArticles"
import { TextBand, bandBg } from "@/components/gisela/TextBand"
import { useLocalePath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

type Photo = HeroPhoto

interface Props {
  condition: { slug: string; parent: string | null; label: string }
  content: {
    hero: { heading?: string; subheading?: string; photo?: Photo | null; _hidden?: boolean }
    // Urgent help. Empty on most pages, and then not rendered at all.
    crisis?: { heading?: string; body?: string }
    recognise: { heading?: string; body?: string }
    approach: { heading?: string; body?: string; photo?: Photo }
    expect: { heading?: string; body?: string }
    faq: { body?: string }
    // The closing pair, on every page but the OCD subtypes.
    cta?: { photo?: Photo | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  articles?: RelatedArticle[]
  /** The home page's tile slot — empty on the subtypes, which don't show it. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

// Shared template for every condition landing page (GiselaConditions) —
// anxiety, EMDR/trauma, health anxiety, and the OCD subtype cluster. All copy
// is CMS-driven; the controller supplies per-condition fallback metadata.
export default function ConditionPage({
  condition,
  content,
  articles,
  tile_photos = {},
  contact_photo,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.condition
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  // The OCD subtype cluster shares the pillar page's closing band, which asks
  // for the free consultation rather than an enquiry.
  const ocdSubtype = condition.parent === "ocd-therapy"
  const title = meta?.title ?? condition.label
  const description = meta?.description ?? ""

  // Copy cross-links to other pages (e.g. the EMDR page pointing at /emdr) —
  // keep them on the visitor's language, as TherapyPage and the index do.
  const markdown: Components = {
    a: ({ href, children }) => <a href={lp(href ?? "")}>{children}</a>,
  }

  // Entry ids are generated per condition (Condition.registry_entries):
  // "<slug>-recognise" and so on.
  const id = (key: string) => `${condition.slug}-${key}`
  const bands = presentBands(
    [
      { id: id("crisis"), present: !!content.crisis?.body,
        render: (i) => (
          /* A ruled card rather than another band, so it reads as an
             interruption on either background colour — the same shape the
             Booking & Fees page's urgent-help callout uses. */
          <section className={`${bandBg(i)} py-12`}>
            <div className="mx-auto max-w-3xl px-6 lg:px-8">
              <div className="rounded-2xl border-l-4 border-teal bg-white p-8 ring-1 ring-hairline">
                <h2>{content.crisis?.heading || t.crisis_heading}</h2>
                <div className="body-content mt-4 text-ink-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdown}>
                    {content.crisis?.body ?? ""}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </section>
        ) },
      { id: id("recognise"), present: !!content.recognise.body,
        render: (i) => <TextBand index={i} heading={content.recognise.heading || t.recognise_heading} body={content.recognise.body} markdown={markdown} /> },
      { id: id("approach"), present: !!content.approach.body,
        render: (i) => <TextBand index={i} heading={content.approach.heading || t.approach_heading} body={content.approach.body} photo={content.approach.photo} markdown={markdown} /> },
      { id: id("expect"), present: !!content.expect.body,
        render: (i) => <TextBand index={i} heading={content.expect.heading || t.expect_heading} body={content.expect.body} markdown={markdown} /> },
      { id: id("faq"), present: !!content.faq.body,
        render: (i) => <TextBand index={i} heading={tc.common_questions} body={content.faq.body} markdown={markdown} /> },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        {description && <meta name="description" content={description} />}
        <meta property="og:title" content={title} />
        {description && <meta property="og:description" content={description} />}
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            {ocdSubtype ? (
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
                <a href={lp("/ocd-therapy")} className="text-white/60 no-underline hover:text-white">
                  {t.back_to_ocd}
                </a>
              </p>
            ) : (
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
                {t.eyebrow}
              </p>
            )}
            <h1 className="text-white">{content.hero.heading || condition.label}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* Everything below the hero reserves the right gutter (xl+) for the
            related-articles sidebar; the bands' full-bleed backgrounds still
            span the viewport because the padding sits on each section. */}
        {/* The gutter keeps a column's worth of height when the sidebar is
            in it: a page whose bands are shorter than its reading list would
            otherwise leave the sticky column nowhere to sit, and it would
            spill over the bands the page closes with. */}
        <div
          className={cn(
            "relative xl:[&>section]:pr-80",
            !!articles?.length && "xl:min-h-[34rem]",
          )}
        >
          {bands.map((band, i) => (
            <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
          ))}

          {/* Articles written for this condition — self-maintaining, and absent
              until one is linked to the page. A band below xl, a sidebar above. */}
          <RelatedArticlesBand articles={articles} className={bandBg(bands.length)} />

          <RelatedArticlesSidebar articles={articles} />
        </div>

        {/* Every condition page ends the way the home page does — the
            invitation, the tiles out of it, then a room beside the short
            form. These sit outside the sidebar's gutter: they are full-width
            bands, and the reading column they would otherwise be squeezed
            into has finished by here. A subtype keeps its own first-person
            wording in the band — the pillar speaks for Gisela, the subtype
            pages speak as her. */}
        {!sectionHidden(content.cta) && (
          <ContactBand
            heading={t.cta_heading}
            body={ocdSubtype ? t.cta_body_ocd : t.cta_body}
            photo={content.cta?.photo}
            label={ocdSubtype ? t.cta_button_ocd : undefined}
          />
        )}

        {/* This page's own tile is not in the row to begin with — the row is
            her top-level pages. */}
        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
            voice={ocdSubtype ? "hers" : "about-her"}
          />
        )}
      </PublicLayout>
    </>
  )
}
